import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Utils } from '@pbt/pbt-ui-components'

import Features from '../../constants/features'
import { getClientType, getCurrentClient } from '../../store/duck/clients'
import {
  getAppointmentTypes,
  getFeatureToggle,
} from '../../store/duck/constants'
import { getIsCheckInFlow } from '../../store/duck/flow'
import {
  editPatient,
  getCurrentPatient,
  getPatientsIsLoading,
  updateCurrentPatient,
} from '../../store/duck/patients'
import { getCurrentAppointment } from '../../store/duck/schedules'
import { useNavigateWithQueryString } from '../../utils'
import useCallbackWhenLoaded from '../../utils/useCallbackWhenLoaded'
import PatientDetailsScreen from './common/PatientDetailsScreen'
import KioskScreen from './KioskScreen'

const KioskPatientDetailsScreen = () => {
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const { t } = useTranslation('Common')

  const patient = useSelector(getCurrentPatient)
  const isCheckIn = useSelector(getIsCheckInFlow)
  const appointment = useSelector(getCurrentAppointment) || {}
  const client = useSelector(getCurrentClient)
  const isLoading = useSelector(getPatientsIsLoading)
  const AppointmentTypes = useSelector(getAppointmentTypes)
  const isNewAppointmentFlowEnabled = useSelector(
    getFeatureToggle(Features.NEW_APPOINTMENT_BOOKING_FLOW_KIOSK),
  )
  const clientType = useSelector(getClientType)

  const patientDetailsRef = useRef()

  const BoardingType = Utils.findConstantIdByName('Boarding', AppointmentTypes)

  const isCreate = !patient?.id
  const isBoarding = appointment.type?.id === BoardingType

  const onPatientUpdated = () => {
    if (isCheckIn) {
      if (isBoarding) {
        navigateWithQueryString({ url: '/patient-diet-and-allergies' })
      } else {
        navigateWithQueryString({ url: '/questions' })
      }
    } else if (appointment.type) {
      navigateWithQueryString({ url: '/primary-complaint' })
    } else {
      navigateWithQueryString({ url: '/appointment-type' })
    }
  }

  const callbackWhenLoaded = useCallbackWhenLoaded(
    onPatientUpdated,
    getPatientsIsLoading,
  )

  const onProceed = () => {
    if (patientDetailsRef.current.validate()) {
      const newPatient = patientDetailsRef.current.getPatient()

      if (isCreate) {
        dispatch(updateCurrentPatient(newPatient))
        if (isNewAppointmentFlowEnabled && clientType === 'new') {
          navigateWithQueryString({ url: '/visit-reason' })
        } else {
          navigateWithQueryString({ url: '/patient-microchipped' })
        }
      } else if (isNewAppointmentFlowEnabled && clientType === 'new') {
        callbackWhenLoaded()
        dispatch(editPatient(client.id, newPatient))
        navigateWithQueryString({ url: '/visit-reason' })
      } else {
        callbackWhenLoaded()
        dispatch(editPatient(client.id, newPatient))
      }
    }
  }

  const handleBack = () => {
    // Save patient details to Redux before navigating away
    if (isNewAppointmentFlowEnabled) {
      if (patientDetailsRef.current && patientDetailsRef.current.saveToRedux) {
        patientDetailsRef.current.saveToRedux()
      }
    }
    if (isNewAppointmentFlowEnabled && clientType === 'new') {
      navigateWithQueryString({ url: '/new-client-details' })
    } else {
      window.history.back()
    }
  }

  return (
    <KioskScreen
      justifyContent="flex-start"
      proceedButtonLabel={
        isNewAppointmentFlowEnabled
          ? t('Common:NEXT_ACTION')
          : t('Common:CONFIRM_ACTION')
      }
      proceedButtonLoading={isLoading}
      title={
        isCreate
          ? t('Common:CAN_YOU_TELL_US_ABOUT_YOUR_PET')
          : t('Common:CONFIRM_PATIENTS_INFORMATION', {
              patientName: patient.name || '',
            })
      }
      onBack={handleBack}
      onProceed={onProceed}
    >
      <PatientDetailsScreen patient={patient} ref={patientDetailsRef} />
    </KioskScreen>
  )
}

export default KioskPatientDetailsScreen
